// External
import _ from "lodash";
import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import PropTypes from "prop-types";
import update from "immutability-helper";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import classNames from "classnames";

// Custom hooks
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import { useHandleScroll } from "Utils/CustomHooks";
import { useMount } from "Hooks";
import { useWorksheetAiConstantPrompts } from "Worksheet/hooks/useWorksheetAiConstantPrompts";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import MarkdownView from "UIComponents/MarkdownView";
import InputTextField from "UIComponents/InputTextField";
import Loading from "UIComponents/Loading";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import AIContextBar from "AppComponents/AIContextBar";
import PromptSuggestionModal from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/components/AddAssignment/AiQuestionAssistant/components/PromptSuggestionModal/PromptSuggestionModal";
import PrerequisiteSelection from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/components/AddAssignment/AiQuestionAssistant/components/PrerequisiteSelection/PrerequisiteSelection";
import PromptAction from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/components/AddAssignment/AiQuestionAssistant/components/PromptAction/PromptAction";
import QuestionFieldComponent from "Worksheet/components/Question/QuestionFieldComponent";

// Design system
import {
  IconButton,
  Avatar,
  Button,
  Tooltip,
  Modal,
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
  Checkbox,
} from "@toddle-edu/ds-web";
import {
  RefreshArrowOutlined,
  Shifu3D,
  CloseOutlined,
  PencilLineOutlined,
  TickLargeOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";
import { colors, fontStyles, elevations } from "@toddle-edu/ds-theme";

// Scss
import classes from "./AIAssessmentTools.scss";

// Utils / Services / Constants
import {
  hideIntercom,
  showIntercom,
  resetAIConversation,
  getVariablesForLocalisedPlannerTemplates,
  updateAIConversation,
} from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  checkScrollAtBottom,
  prepareLoadMoreFeed,
  scrollIntoView,
} from "Utils";
import { getIsFromClassroom } from "UnitPlans/modules/Utils";
import {
  updateAssessmentTemplate,
  getUnitDetailsFromUnitPlanField,
  getFieldValue,
  combineUnitPlanMultiFieldData,
  getUnitPlanFromAssessment,
  updateTemplateForLearningGoals,
} from "Assessments/modules/Utils";
import {
  getBlockActions,
  getIsAIResponseLoading,
  getIsListTitleBold,
  checkIsLEAIUserMessageEditEnabled,
  getQuestionActionOptions,
  getToolTypeFromContent,
  isConvertToAlternateAction,
  getAlternateQuestionTypes,
  getUpdatedPrompts,
  getWorksheetAIAssessmentToolsStyle,
  getTemplateArray,
} from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/Utils";
import {
  WRITING,
  LOADING,
  BLOCK_ACTIONS,
  WORKSHEET,
} from "Constants/stringConstants";
import {
  DIALOGS_INFO,
  CONVERT_QUESTION_FORMAT,
  QUESTION_TYPE_UID_VALUE_MAPPING,
  CF_NEW_QUESTION_TYPE,
  CF_TYPE_OF_QUESTION,
  CF_QUESTION_TEXT,
  WS_AI_SETTING_FLAGS,
} from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/Constants";
import {
  AI_TRANSLATION_LANGUAGES,
  ASSESSMENT_TOOLS_OBJ,
} from "@toddle-edu/common-utils";
import { updateUnitPlanTemplate } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/components/PrerequisiteSelection/Utils";
import {
  formatAiFillResponse,
  getAssessmentQuestionsQueryVariables,
  getLearningGoalsFields,
  isFillInTheBlankRes,
  replaceLearningGoalsFields,
} from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import {
  QUESTION_TAG_LOCALES,
  WS_AI_QUESTION_TAGS,
  CHOICE_REGEX,
  CORRECT_ANSWER_REGEX,
  WS_ASSISTANT_QUESTIONS_REGEX,
  FIELD_QUESTION_REGEX,
  TYPE_OF_QUESTION_REGEX,
  QUESTION_TEXT_REGEX,
  CHOICE_TAGS_REGEX,
} from "Worksheet/modules/WorksheetConstants";
import { TEMP_ID } from "AppComponents/AIContextBar/modules/Constants";

// Module
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getUserId,
} from "modules/CommonSelectors";
import { getLocalisedTemplate } from "IBPlanner/modules/IBPlannerModule";
import {
  updateAssignmentField,
  cancelRequest,
  onSendQuery,
  updateLocalFields,
} from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";
import { getSettingValue } from "Utils/ConfigurationUtils";

// GraphQL
import {
  getPlannerTemplateFromCache,
  getStaffBasicDetailsFromCache,
} from "modules/CommonGraphqlHelpers";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";

import { getAIConversationsQuery } from "Worksheet/modules/WorksheetQueries";
import { getAIConversationsFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import { createAIConversation } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/LessonPlanningAssistantModule";

const styles = {
  textAreaInput: {
    fontSize: "1.4rem",
    lineHeight: "2.4rem",
    padding: "12px 76px 12px 52px",
    ...fontStyles.medium,
  },
  popoverContainerStyle: {
    zIndex: 1000,
  },
  overlayInnerStyle: {
    maxWidth: "240px",
  },
  containerStyle: {
    padding: "0",
    backgroundColor: colors.white,
  },
  inputContainerStyle: {
    flex: "1",
  },
  inputStyle: {
    outline: "none",
    border: "0px",
    padding: "0px",
    borderRadius: "0px",
    fontSize: "1.4rem",
    lineHeight: "2rem",
    color: colors.textDefault,
    ...fontStyles.medium,
  },
  customTextStyle: {
    fontSize: "14px",
    color: colors.textDefault,
    ...fontStyles.demiBold,
    fontWeight: "500",
    lineHeight: "20px",
  },
  closeArrowIconStyle: {
    color: colors.neutral400,
  },
};

const markdownComponents = {
  hr: () => <p>{"-----"}</p>,
};

function getAlphabetIndexing(number) {
  return String.fromCharCode(96 + number);
}

const { CHOICES_TAG, CORRECT_ANSWER_TAG } = WS_AI_QUESTION_TAGS;
function processXmlTags(input, t) {
  // Match starting tags
  const startingTagsRegex = /<([^\/>]+)>/g;
  // Remove <questionText> tag
  let output = input.replace(QUESTION_TEXT_REGEX, "");

  // Remove empty tags and replace starting tags with "**Tag Name**\n"
  output = output
    .replace(/<([^/>]+)>\s*<\/\1>/g, "")
    .replace(/(\s*\n\s*)+/g, "\n")
    .replace(/--- \([a-zA-Z0-9@]+\)/g, "-");
  if (_.includes(input, `<${CHOICES_TAG}>`)) {
    output = output
      .replace(CHOICE_REGEX, (withTag, choicesContent) => {
        const choices = choicesContent
          .trim()
          .split("\n")
          .map(choice => choice.trim());
        const numberedChoices = _.map(
          choices,
          (choice, index) =>
            `  ${getAlphabetIndexing(index + 1)}) ${_.replace(
              choice,
              "-- ",
              ""
            )}`
        );

        return `\n**${t(
          QUESTION_TAG_LOCALES[CHOICES_TAG]
        )}**\n\n${numberedChoices.join("\n")}`;
      })
      .replace(CORRECT_ANSWER_REGEX, (_, correctAnswerIndex) => {
        const choices = output
          .match(CHOICE_REGEX)[0]
          .replace(CHOICE_TAGS_REGEX, "")
          .trim()
          .split("\n")
          .map(choice => choice.trim().replace("-- ", ""));
        const correctChoice = choices[parseInt(correctAnswerIndex)];
        return `\n**${t(
          QUESTION_TAG_LOCALES[CORRECT_ANSWER_TAG]
        )}**\n\n  ${getAlphabetIndexing(
          parseInt(correctAnswerIndex) + 1
        )}) ${correctChoice}\n`;
      });
  }

  output = output
    .replace(/<\/[^>]+>/g, "")
    .replace(startingTagsRegex, (_, tagName) => {
      // Capitalize the first letter of the first word in the tag name
      const tagData = QUESTION_TAG_LOCALES[tagName]
        ? t(QUESTION_TAG_LOCALES[tagName])
        : "";
      return `\n**${tagData}**\n\n`;
    });
  3;

  return output;
}

function filterQuestionsAndPlainText(inputText) {
  const separator = "###";

  const parts = inputText.split(separator);

  const result = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    const matches = part.match(WS_ASSISTANT_QUESTIONS_REGEX) || [];

    if (matches?.length > 0) {
      const questions = matches.map((match, index) => ({
        text: match
          .replace(FIELD_QUESTION_REGEX, "")
          .replace(TYPE_OF_QUESTION_REGEX, ""),
        type: "question",
        count: index + 1,
      }));

      result.push(...questions);
    } else if (part.length > 0) {
      result.push({ text: part, type: "plaintext", count: "" });
    }
  }

  return result;
}

const AIAssessmentTools = memo(props => {
  const {
    t,
    portalType,
    assessmentId,
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    messages,
    assessmentToolConversationId,
    resetAIConversation,
    onSendQuery,
    gradeId,
    assessmentDetails,
    isInitialState,
    course,
    middlePaneRef,
    mode,
    entityType,
    unitPlanId,
    sourceFrom,
    isFromUnitPlan,
    courseId,
    unitPlanDetails,
    unitPlanTemplate: _unitPlanTemplate,
    assessmentTemplate,
    isQuestionAssistant,
    creationMode,
    isDraft,
    selectedCourse,
    conversationMetadata,
    conversationAttachments,
    question,
    updateAIConversation,
    learningGoalsFields,
    updateLocalFields,
    updateSettings,
    templateId,
    onClose,
    updateAssignmentField,
    assessmentTools,
  } = props;

  const {
    data: { allPrompts: constantPrompts },
  } = useWorksheetAiConstantPrompts({
    assessmentDetails,
    entityType: WORKSHEET,
    entityId: assessmentId,
  });

  const showDocEditor = getIsWorksheetDocEditorEnabled();

  const [query, setQuery] = useState("");
  const [currentAIState, setCurrentAIState] = useState("");
  const [currentDialogAction, setCurrentDialogueAction] = useState({
    action: "",
    params: {
      isChecked: false,
    },
  });
  const [selectedAction, setSelectedAction] = useState({});
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [promptSuggestionModalInfo, setPromptSuggestionModalInfo] = useState({
    showModal: false,
    params: {},
  });
  const [editUserPromptObj, setEditUserPromptObj] = useState({
    isEditMode: false,
    userPrompt: "",
  });
  const [hoveredMessageId, setHoveredMessageId] = useState(null);

  const [aiVoiceModalLanguage, setAiVoiceModalLanguage] = useState("en");
  const [context, setContext] = useState({});
  const [excludedFields, setExcludedFields] = useState([]);

  const [isAnalysing, setIsAnalysing] = useState(false);
  const [sendButtonParams, setSendButtonParams] = useState({});

  const containerRef = useRef(null);
  const scrollEndRef = useRef(null);

  const { isEditMode = false, userPrompt } = editUserPromptObj;

  const ALTERNATE_QUESTION_TYPES = getAlternateQuestionTypes(t);

  const lastUserMessageId = _.get(
    _.findLast(messages, ({ node }) => node?.authorType === "user"),
    "node.id"
  );

  // whenever the user changes the language in the AI voice modal then
  // set the selected language in local state and load that language bundle
  const setConfigureAIVoiceRef = useCallback(async node => {
    const { preferredLanguageOptions: languageOptions, preferredVoiceObj } =
      node || {};

    const selectedLanguage = _.get(preferredVoiceObj, "language", "en");

    const languageKey = _.find(languageOptions, {
      key: selectedLanguage,
    })?.lng;

    if (languageKey) {
      setAiVoiceModalLanguage(languageKey);
    }
  }, []);

  const resolvedSubjectNodes = _.get(
    _.find(_.get(assessmentDetails, "allFields", []), {
      uid: "subjects",
    }),
    "resolvedMinimalTree.subjects",
    []
  );

  const resolvedSubjectGroupNodes = _.map(resolvedSubjectNodes, "subjectGroup");

  // side effects

  // hide intercom because it is coming above chat send button
  useEffect(() => {
    const { showIntercom, hideIntercom } = props;
    hideIntercom();
    return () => {
      showIntercom();
    };
  }, []);

  // on message sent scroll to bottom
  useEffect(() => {
    if (!_.isEmpty(messages)) {
      scrollToBottom();
    }
  }, [messages, shouldScrollToBottom, currentAIState]);

  // on selection selected send it as a message
  useEffect(() => {
    if (!_.isEmpty(selectedAction) && _.isEmpty(selectedAction?.label)) {
      onSendMessage();
    }
  }, [selectedAction]);

  useEffect(() => {
    if (
      _.get(sendButtonParams, "attachmentHasTempId") &&
      _.every(conversationAttachments, ({ id }) => !_.includes(id, TEMP_ID))
    ) {
      onSendMessage();
    }
  }, [conversationAttachments, sendButtonParams?.attachmentHasTempId]);

  useEffect(() => {
    return () => {
      onClickStop();
    };
  }, []);

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  // used for update local fields into conversation metadata
  useEffect(() => {
    updateLocalFields({
      aiConversationId: assessmentToolConversationId,
      context,
      conversationMetadata,
    });
  }, [context]);

  // update conversation metadata's localFields data in context
  useMount(() => {
    const localFields = _.get(conversationMetadata, "localFields", []);

    const localFieldsContext = _.reduce(
      localFields,
      (acc, { field, value }) => {
        _.set(acc, field, value);
        return acc;
      },
      {}
    );

    setContext(prev => ({ ...prev, ...localFieldsContext }));
  });

  const unitPlanTemplate = _.get(
    getLocalisedTemplate({
      template: { body: _.cloneDeep(_unitPlanTemplate) },
      t,
      templateType: "unitPlan",
    }),
    "body",
    {}
  );

  const fieldList = _.get(assessmentTemplate, "field_list", {});
  const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});

  const stepList = _.get(assessmentTemplate, "step_list", {});
  const unitPlanStepList = _.get(unitPlanTemplate, "step_list", {});

  // functions
  const scrollToBottom = () => {
    if (scrollEndRef.current && shouldScrollToBottom) {
      scrollIntoView({ element: scrollEndRef.current });
    }
  };

  const updatePromptSuggestionModalInfo = params => {
    setPromptSuggestionModalInfo(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const onPromptSelection = prompt => {
    setSelectedAction(prompt);
  };

  const onDialogConfirm = async () => {
    const { action, params } = currentDialogAction || {};

    switch (action) {
      case "CLEAR_CHAT":
        {
          // 1. show full screen loader
          setCurrentAIState(LOADING);

          // 2. reset the AI conversation
          await resetAIConversation({
            input: {
              id: assessmentToolConversationId,
            },
          });

          // 3. update the selected language in the AI conversation and reset promptFields
          const selectedLanguage = _.find(AI_TRANSLATION_LANGUAGES, {
            code: aiVoiceModalLanguage,
          });

          const metadata = {
            allFields: [],
            localFields: [],
          };

          if (!_.isEmpty(selectedLanguage)) {
            // 3. update the selected language in the AI conversation and reset promptFields
            const selectedLanguage = _.find(AI_TRANSLATION_LANGUAGES, {
              code: aiVoiceModalLanguage,
            });

            _.set(metadata, "language", _.get(selectedLanguage, "label"));
            _.set(metadata, "languageCode", aiVoiceModalLanguage);
            _.set(
              metadata,
              "selectedDropdownLanguage",
              _.get(selectedLanguage, "id")
            );
          }

          await updateAIConversation({
            input: {
              id: assessmentToolConversationId,
              metadata,
            },
          });

          // 4. reset states
          setCurrentAIState("");
          setExcludedFields([]);
        }
        break;

      case "REPLACE_ASSESSMENT_TOOL":
        {
          if (params?.isChecked) {
            updateSettings({
              settings: [
                {
                  name:
                    WS_AI_SETTING_FLAGS.HIDE_REPLACE_ASSESSMENT_TOOL_DIALOGUE,
                  value: "true",
                },
              ],
              showToastMsg: false,
            });
          }
          setCurrentAIState(LOADING);
          await updateAssignmentField(params);
          setCurrentAIState("");
        }
        break;
    }

    setCurrentDialogueAction({ action: "" });
  };

  const onSendMessage = async data => {
    const isUserMessageEdited = _.get(data, "isUserMessageEdited", false);
    const { checkLoadingState = true } = sendButtonParams;

    if (getIsAIResponseLoading(currentAIState) && checkLoadingState) return;

    const lastMessageId = _.get(_.last(messages), "node.id");
    await onSendQuery({
      t,
      query,
      leId: assessmentId,
      aiConversationId: assessmentToolConversationId,
      selectedAction,
      setQuery,
      setSelectedAction,
      setCurrentAIState,
      gradeId,
      course,
      lastMessageId,
      isUserMessageEdited,
      editUserPromptObj,
      setEditUserPromptObj,
      messages,
      language: aiVoiceModalLanguage,
      context,
      setContext,
      fieldList,
      unitPlanFieldList,
      unitPlanDetails,
      assessmentDetails,
      excludedFields,
      entityType,
      stepList,
      unitPlanStepList,
      isQuestionAssistant,
      conversationMetadata,
      conversationAttachments,
      learningGoalsFields,
      setIsAnalysing,
      setSendButtonParams,
      sendButtonParams,
    });

    // * omit extra fields from context once action performed
    if (isQuestionAssistant) {
      const toBeRemoved = [CF_NEW_QUESTION_TYPE, CF_QUESTION_TEXT];
      const anyFieldPresent = _.some(toBeRemoved, key => _.has(context, key));

      if (anyFieldPresent) {
        setContext(prevContext => _.omit(prevContext, toBeRemoved));
      }
    }
  };

  const onClickStop = () => {
    cancelRequest?.();
    setCurrentAIState("");
  };

  const onScrollBodyContainer = e => {
    setShouldScrollToBottom(checkScrollAtBottom(e.target, 56)); // ignore paddingBottom of 56px
  };

  const getMessageLoader = ({ message, isInline = false }) => {
    const messageLoaderClasses = classNames({
      [classes.generatingMsg]: true,
      [classes.inlineMessageLoader]: isInline,
    });

    const messageClasses = classNames({
      [classes.generatingSingleMsg]: true,
      [classes.inlineLoaderMsg]: isInline,
    });

    const messageLoader = (
      <div className={messageLoaderClasses}>
        <div className={messageClasses}>{message}</div>
        <div className={classes.dot} />
      </div>
    );

    if (isInline) {
      return messageLoader;
    }

    return (
      <div className={classes.assistantMsg}>
        <div className={classes.generatingContent}>
          <Shifu3D width={40} height={40} className={classes.assistantAvatar} />
          <div className={classes.generatingMsgContainer}>{messageLoader}</div>
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    return (
      <React.Fragment>
        {_.map(messages, ({ node }, index) => {
          const {
            id,
            authorType,
            content,
            metadata,
            createdBy,
            votes,
            usageMetricsId,
          } = node || {};

          const { firstName, lastName, profileImage } = createdBy || {};
          const { language } = metadata || {};

          const isLastUserMessage = lastUserMessageId === id;
          const showButton = isEditMode || hoveredMessageId === id;
          const showEditMessageIcon =
            checkIsLEAIUserMessageEditEnabled() &&
            isLastUserMessage &&
            metadata.isUserInputMessage &&
            showButton;

          const assessmentToolType = getToolTypeFromContent({
            language: language || aiVoiceModalLanguage,
            content,
            assessmentDetails,
            metadata,
          });

          switch (authorType) {
            case "user": {
              return (
                <div
                  key={id}
                  className={classes.messageContainer}
                  onMouseEnter={() => setHoveredMessageId(id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                >
                  <div className={classes.userMsg}>
                    <Avatar
                      size={"medium"}
                      shape={"square"}
                      shade={"light"}
                      className={classes.noShrink}
                      name={`${firstName} ${lastName}`}
                      src={profileImage}
                    />
                    {isEditMode && isLastUserMessage ? (
                      <InputTextField
                        value={userPrompt}
                        name={"userPrompt"}
                        inputStyle={styles.inputStyle}
                        containerStyle={styles.inputContainerStyle}
                        updateInputField={params =>
                          setEditUserPromptObj({
                            ...editUserPromptObj,
                            ...params,
                          })
                        }
                      />
                    ) : (
                      <div className={classes.singleMsg}>{content}</div>
                    )}
                  </div>
                  {showEditMessageIcon && (
                    <div className={classes.buttonContainer}>
                      {isEditMode && isLastUserMessage ? (
                        <>
                          <IconButton
                            icon={<CloseOutlined />}
                            dsVersion="2.0"
                            type="plain"
                            variant="neutral"
                            size="medium"
                            _legacy_variant="plain"
                            _legacy_size="small"
                            onClick={() =>
                              setEditUserPromptObj({
                                isEditMode: false,
                                userPrompt: "",
                              })
                            }
                            testId={
                              "worksheet-worksheetAssistant-closeEditPrompt"
                            }
                          />
                          <IconButton
                            icon={<TickLargeOutlined />}
                            dsVersion="2.0"
                            type="fill"
                            variant="progressive"
                            size="medium"
                            _legacy_variant="progressive"
                            _legacy_size="small"
                            onClick={() => {
                              onSendMessage({ isUserMessageEdited: true });
                              setEditUserPromptObj({
                                ...editUserPromptObj,
                                isEditMode: false,
                              });
                            }}
                            testId={
                              "worksheet-worksheetAssistant-sendUserPrompt"
                            }
                          />
                        </>
                      ) : (
                        <IconButton
                          icon={<PencilLineOutlined />}
                          dsVersion="2.0"
                          type="plain"
                          variant="neutral"
                          size="medium"
                          _legacy_variant="plain"
                          _legacy_size="x-small"
                          onClick={() =>
                            setEditUserPromptObj({
                              isEditMode: true,
                              userPrompt: content,
                              lastUserMessageId,
                            })
                          }
                          testId={"worksheet-worksheetAssistant-editUserPrompt"}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            }

            case "assistant": {
              let blocks = null;

              const {
                userInputFlagged,
                model,
                temperature,
                intentUsageMetricsId,
              } = metadata || {};

              const blockContentClass = classNames({
                [classes.blockContentText]: true,
                [classes.boldMarker]: getIsListTitleBold(content),
              });

              const dividedChunks = filterQuestionsAndPlainText(content);
              blocks = _.map(dividedChunks, chunk => {
                const { text, type, count } = chunk || {};

                let updatedText = text;
                if (isFillInTheBlankRes(updatedText)) {
                  updatedText = formatAiFillResponse(updatedText);
                }
                updatedText = processXmlTags(updatedText, t);
                updatedText = _.replace(updatedText, /\\\\/g, "\\");

                return {
                  component: (
                    <MarkdownView
                      content={updatedText}
                      contentContainerClass={blockContentClass}
                      components={markdownComponents}
                    />
                  ),
                  type,
                  text,
                  count,
                };
              });

              const onClickAction = async ({ key }) => {
                if (_.isEqual("add_assessment_tool", key)) {
                  const isToolExist = !_.isEmpty(
                    _.find(assessmentTools, ({ node }) => {
                      return (
                        _.get(node, "assessmentToolType") === assessmentToolType
                      );
                    })
                  );

                  // check if previously user had selected "do not show this message again" checkbox
                  // if yes then do not show the dialog
                  const hasUserReplaceDialogHide = getSettingValue({
                    name:
                      WS_AI_SETTING_FLAGS.HIDE_REPLACE_ASSESSMENT_TOOL_DIALOGUE,
                    readFromUserAndOrgLevel: true,
                  });

                  const toolsParams = {
                    selectedAssessmentField: assessmentToolType,
                    content,
                    assessmentId: _.get(assessmentDetails, "id"),
                    key: "add_to_ws",
                    assessmentDetails,
                    metadata,
                  };

                  if (isToolExist && !hasUserReplaceDialogHide) {
                    setCurrentDialogueAction({
                      action: "REPLACE_ASSESSMENT_TOOL",
                      params: toolsParams,
                    });
                  } else {
                    setCurrentAIState(BLOCK_ACTIONS);
                    await updateAssignmentField(toolsParams);
                    setCurrentAIState("");
                  }
                }
              };

              const getActions = ({ messageId, blockIndex }) => {
                const actionsList = getBlockActions({
                  isQuestionAssistant,
                  assessmentToolType,
                  t,
                  isAIAssessmentTools: true,
                });

                const isActionsDisabled = !_.isEmpty(currentAIState);
                return (
                  <div className={classes.blockActions}>
                    {_.map(actionsList, action => {
                      const {
                        key,
                        label,
                        icon,
                        disabled = false,
                        disabledTooltip,
                        isLocalised = false,
                        tooltipProps = {},
                        localisationParams,
                      } = action || {};

                      return (
                        <ConditionalWrapper
                          key={key}
                          type={"wrapperAsElement"}
                          condition={disabled && !_.isEmpty(disabledTooltip)}
                          wrapper={
                            <Tooltip
                              placement={"bottom"}
                              tooltip={
                                isLocalised
                                  ? disabledTooltip
                                  : localisationParams
                                  ? t(disabledTooltip, localisationParams)
                                  : t(disabledTooltip)
                              }
                              overlayInnerStyle={styles.overlayInnerStyle}
                              {...tooltipProps}
                            />
                          }
                        >
                          {key === "add_or_replace" ? (
                            <Dropdown
                              overlay={
                                <DropdownMenu
                                  options={getQuestionActionOptions(t)}
                                  onClick={option =>
                                    onClickAction({
                                      key: _.get(option, "key"),
                                      messageId,
                                      blockIndex,
                                    })
                                  }
                                  testId={
                                    "worksheet-worksheetAssistant-onClickAction"
                                  }
                                />
                              }
                              disabled={isActionsDisabled}
                            >
                              <ButtonDisclosure
                                dsVersion={"2.0"}
                                type={"plain"}
                                size={"medium"}
                                variant={"neutral"}
                                _legacy_size={"small"}
                                _legacy_variant={"plain"}
                                icon={icon}
                                isCompact={true}
                                className={classes.actionBtn}
                                disabled={isActionsDisabled}
                                testId={
                                  "worksheet-worksheetAssistant-addReplaceAction"
                                }
                              >
                                {t(label)}
                              </ButtonDisclosure>
                            </Dropdown>
                          ) : (
                            <Button
                              key={key}
                              dsVersion={"2.0"}
                              type={"plain"}
                              variant={"neutral"}
                              size={"medium"}
                              _legacy_size={"small"}
                              _legacy_variant={"inline"}
                              icon={icon}
                              onClick={() =>
                                onClickAction({
                                  key,
                                  messageId,
                                  blockIndex,
                                })
                              }
                              disabled={disabled || isActionsDisabled}
                              testId={
                                "worksheet-worksheetAssistant-openDropdown"
                              }
                            >
                              {localisationParams
                                ? t(label, localisationParams)
                                : t(label)}
                            </Button>
                          )}
                        </ConditionalWrapper>
                      );
                    })}
                  </div>
                );
              };

              const isLastMessage = _.size(messages) - 1 === index;
              const showActions =
                !userInputFlagged &&
                (!isLastMessage ||
                  (isLastMessage && !getIsAIResponseLoading(currentAIState)));

              const blockClass = classNames({
                [classes.block]: true,
                [classes.textCritical]: userInputFlagged,
              });

              const blockWrapperClass = classNames({
                [classes.blockWrapper]: true,
                [classes.violationBlock]: userInputFlagged,
              });

              if (_.isEmpty(blocks) && isAnalysing) {
                return getMessageLoader({
                  message: t("worksheet:analysing"),
                });
              }

              return _.size(blocks) ? (
                <div key={id} className={classes.assistantMsg}>
                  <div className={classes.assistantMsgUpper}>
                    <Shifu3D
                      width={40}
                      height={40}
                      className={classes.assistantAvatar}
                    />
                  </div>
                  <div className={classes.blockWrapperParentContainer}>
                    {_.map(blocks, ({ component, type, text }, blockIndex) => {
                      const showAnalysingLoader = isAnalysing && isLastMessage;

                      const isAssessmentTool = _.has(
                        ASSESSMENT_TOOLS_OBJ,
                        assessmentToolType
                      );

                      return (
                        <div className={blockWrapperClass} key={blockIndex}>
                          <div className={blockClass}>
                            <div>
                              {component}
                              {showAnalysingLoader
                                ? getMessageLoader({
                                    message: t("worksheet:analysing"),
                                    isInline: true,
                                  })
                                : null}
                            </div>
                          </div>
                          {showActions &&
                          (!_.isEmpty(type) || isAssessmentTool) ? (
                            <div className={classes.actionsContainer}>
                              {isAssessmentTool ? (
                                getActions({
                                  questionText: text,
                                  messageId: id,
                                  authorType,
                                  blockIndex,
                                })
                              ) : (
                                <p></p>
                              )}
                              <AIFeedbackStrip
                                showWarningText={false}
                                containerStyle={styles.containerStyle}
                                defaultFeedbackStatus={votes.myVote}
                                usageMetricsId={usageMetricsId}
                                model={model}
                                temperature={temperature}
                                intentUsageMetricsId={intentUsageMetricsId}
                                sourceFrom={"LESSON_PLANNING_ASSISTANT"}
                                showLikeButton={false}
                                showDislikeButton={false}
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null;
            }

            case "prerequisite": {
              const isLastMessage = _.size(messages) - 1 === index;
              const isLastMessagePrerequisite =
                isLastMessage && !getIsAIResponseLoading(currentAIState);

              if (!isLastMessagePrerequisite) return null;

              return (
                <div className={classes.assistantMsg}>
                  <PrerequisiteSelection
                    t={t}
                    prerequisiteFieldsData={metadata || {}}
                    portalType={portalType}
                    fieldList={fieldList}
                    stepList={stepList}
                    assessmentDetails={assessmentDetails}
                    unitPlanId={unitPlanId}
                    context={context}
                    setContext={setContext}
                    mode={mode}
                    middlePaneRef={middlePaneRef}
                    isFromUnitPlan={isFromUnitPlan}
                    sourceFrom={sourceFrom}
                    setSelectedAction={setSelectedAction}
                    messages={messages}
                    courseId={courseId}
                    unitPlanTemplate={unitPlanTemplate}
                    unitPlanDetails={unitPlanDetails}
                    course={course}
                    excludedFields={excludedFields}
                    setExcludedFields={setExcludedFields}
                    isQuestionAssistant={isQuestionAssistant}
                    conversationMetadata={conversationMetadata}
                    aiConversationId={assessmentToolConversationId}
                  />
                </div>
              );
            }
          }
        })}

        {currentAIState === WRITING
          ? getMessageLoader({ message: t("classRoom:writing") })
          : null}
      </React.Fragment>
    );
  };

  const renderHeader = () => {
    const headerClasses = classNames(classes.header, classes.whiteBackground);

    return (
      <header className={headerClasses}>
        <div className={classes.flexWithGap}>
          <Shifu3D width={40} height={40} className={classes.assistantAvatar} />
          <div className={"text-label-2"}>{t("common:ai_assistant")}</div>
        </div>
        <div className={classes.flexWithGap}>
          <Tooltip
            tooltip={t("common:clear_chat_history")}
            placement={"bottom"}
          >
            <IconButton
              dsVersion="2.0"
              type="plain"
              variant="neutral"
              size="medium"
              _legacy_size={"medium"}
              _legacy_variant={"plain"}
              disabled={
                getIsAIResponseLoading(currentAIState) || _.isEmpty(messages)
              }
              icon={<RefreshArrowOutlined />}
              onClick={() => {
                setCurrentDialogueAction({
                  action: "CLEAR_CHAT",
                  params: {},
                });
              }}
              testId={"worksheet-worksheetAssistant-clearChat"}
            />
          </Tooltip>

          <IconButton
            dsVersion="2.0"
            type="plain"
            variant="neutral"
            size="medium"
            _legacy_size={"medium"}
            _legacy_variant={"plain"}
            icon={<CloseOutlined />}
            onClick={onClose}
            testId={"worksheet-worksheetAssistant-closeAssistantRightPane"}
          />
        </div>
      </header>
    );
  };

  const renderBodyContainerEnd = () => {
    if (getIsAIResponseLoading(currentAIState)) {
      return <div className={classes.scrollEnd} ref={scrollEndRef} />;
    } else {
      const showDisclaimerText =
        !isInitialState &&
        _.some(messages, ({ node }) => node?.authorType === "assistant");

      if (showDisclaimerText) {
        const disclaimerTextClass = classNames({
          [classes.disclaimerText]: true,
          [classes.margin24]: _.last(messages)?.node?.authorType === "user",
        });

        return (
          <div className={disclaimerTextClass} ref={scrollEndRef}>
            <InformationOutlined variant={"subtle"} size={"xxx-small"} />
            {t("common:ai_responses_disclaimer_msg")}
          </div>
        );
      }
    }

    return null;
  };

  const renderAssessmentToolPrompts = () => {
    const promptOptions = getUpdatedPrompts({
      t,
      value: constantPrompts,
      isEnglishLanguageSelected: _.isEqual(aiVoiceModalLanguage, "en"),
      isAIAssessmentTools: true,
    });

    const handleQuestionPromptsClick = prompt => {
      let selectedPrompt = prompt;
      const selectedPromptKey = _.get(prompt, "key", "");

      if (!_.isEmpty(query)) {
        setQuery("");
      }

      if (isConvertToAlternateAction(selectedPromptKey, t)) {
        const newQuestionType = _.get(
          _.find(ALTERNATE_QUESTION_TYPES, {
            key: selectedPromptKey,
          }),
          "value"
        );

        setContext({
          ...context,
          [CF_NEW_QUESTION_TYPE]: newQuestionType,
          [CF_TYPE_OF_QUESTION]: _.get(QUESTION_TYPE_UID_VALUE_MAPPING),
        });

        selectedPrompt = _.find(promptOptions, {
          action: CONVERT_QUESTION_FORMAT,
        });
      }

      setSelectedAction(selectedPrompt);
    };
    return (
      <div className={classes.assistantMsg}>
        <div className={classes.assistantMsgUpper}>
          <Shifu3D width={40} height={40} className={classes.assistantAvatar} />
        </div>
        <div className={classes.questionPromptsContainer}>
          <div className={classes.questionTitleContainer}>
            <QuestionFieldComponent
              type={"RichText_Ported"}
              fieldValue={t("worksheet:create_assessment_tool_prompt")}
              fieldMode={"view"}
              portalType={portalType}
              customTextStyle={styles.customTextStyle}
              textStyle={styles.customTextStyle}
              applyDefaultClass={!showDocEditor}
            />
          </div>

          <PromptAction
            promptItems={promptOptions}
            onPromptActionClick={handleQuestionPromptsClick}
            isActionsDisabled={!_.isEmpty(currentAIState)}
          />
        </div>
      </div>
    );
  };

  const onClickFooterOption = id => {
    switch (id) {
      case "suggestion_library": {
        updatePromptSuggestionModalInfo({
          showModal: true,
          params: {},
        });
      }
    }
  };

  const onClickDoNotShowCheckbox = () => {
    const { isChecked } = dialogParams || {};
    setCurrentDialogueAction({
      action: dialogAction,
      params: {
        ...dialogParams,
        isChecked: !isChecked,
      },
    });
  };

  const stopGeneratingBtnClasses = classNames(
    classes.stopGeneratingBtn,
    classes.revampStopGeneratingBtn
  );

  const footerTopComp =
    getIsAIResponseLoading(currentAIState) &&
    currentDialogAction?.action !== "CLEAR_CHAT" ? (
      <Button
        className={stopGeneratingBtnClasses}
        dsVersion={"2.0"}
        _legacy_size={"medium"}
        size={"medium"}
        _legacy_variant={"outlined-subtle"}
        variant={"neutral"}
        type={"outlined"}
        onClick={onClickStop}
        style={elevations["elevation-2B"]}
      >
        {t("common:stop_with_label", {
          label: t("classRoom:generating"),
        })}
      </Button>
    ) : null;

  const isSendButtonDisabled =
    getIsAIResponseLoading(currentAIState) ||
    (_.isEmpty(selectedAction) && _.isEmpty(query));

  const { action: dialogAction, params: dialogParams = {} } =
    currentDialogAction || {};

  // classes

  const bodyContainerClass = classNames({
    [classes.bodyContainer]: true,
    [classes.bodyContainerWithMessages]: !isInitialState,
  });

  const innerContainerClass = classNames({
    [classes.innerContainer]: true,
    [classes.innerContainerWithMessages]: !isInitialState,
  });

  const containerClasses = classNames(
    classes.container,
    classes.containerRevamp
  );

  return (
    <div className={containerClasses} ref={containerRef}>
      <div className={innerContainerClass}>
        {renderHeader()}

        <div className={bodyContainerClass} onScroll={onScrollBodyContainer}>
          {renderAssessmentToolPrompts()}
          {!_.isEmpty(messages) ? renderMessages() : null}
          {renderBodyContainerEnd()}
        </div>
      </div>

      <AIContextBar
        selectedAction={selectedAction}
        setSelectedAction={setSelectedAction}
        value={query}
        onChange={setQuery}
        isInitialState={isInitialState}
        onSendMessage={onSendMessage}
        isSendButtonDisabled={isSendButtonDisabled}
        footerTopComp={footerTopComp}
        context={context}
        setContext={setContext}
        excludedFields={excludedFields}
        setExcludedFields={setExcludedFields}
        creationMode={creationMode}
        sourceFrom={sourceFrom}
        mode={mode}
        isFromUnitPlan={isFromUnitPlan}
        middlePaneRef={middlePaneRef}
        idDraft={isDraft}
        portalType={portalType}
        onClickFooterOption={onClickFooterOption}
        isWritingMessage={getIsAIResponseLoading(currentAIState)}
        setConfigureAIVoiceRef={setConfigureAIVoiceRef}
        courseId={courseId}
        selectedCourse={selectedCourse}
        leId={assessmentId}
        conversationMetadata={conversationMetadata}
        conversationAttachments={conversationAttachments}
        isQuestionAssistant={true}
        isAIAssessmentTools={true}
        question={question}
        aiConversationId={assessmentToolConversationId}
        chatInputPlaceholder={
          !_.isEmpty(selectedAction)
            ? t("worksheet:type_additional_instructions_here")
            : t("worksheet:chat_here_with_toddle_ai")
        }
        templateId={templateId}
      />

      <Modal
        isOpen={!_.isEmpty(dialogAction)}
        onClose={() => setCurrentDialogueAction({ action: "" })}
        width={"440px"}
        height={"fit-content"}
      >
        <Modal.Header
          title={DIALOGS_INFO?.[dialogAction]?.title({
            t,
            ...dialogParams,
          })}
          isClosable={true}
        />

        <Modal.Body>
          <div className={classes.modalBody}>
            {DIALOGS_INFO?.[dialogAction]?.message({
              t,
              ...dialogParams,
            })}

            {DIALOGS_INFO?.[dialogAction]?.isDoNotShowAgainPopupVisible ? (
              <div
                className={classes.modalCheckboxContainer}
                onClick={onClickDoNotShowCheckbox}
                onKeyDown={e =>
                  onEnterOrSpaceKeyDown(e, onClickDoNotShowCheckbox)
                }
                role="checkbox"
                aria-checked={dialogParams?.isChecked}
                tabIndex={0}
              >
                <Checkbox isChecked={dialogParams?.isChecked} tabIndex={-1} />
                <span>{t("common:dont_show_this_message_again")}</span>
              </div>
            ) : null}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <footer className={classes.modalFooter}>
            <Button
              dsVersion="2.0"
              variant={"neutral"}
              type={"outlined"}
              size={"large"}
              _legacy_size={"large"}
              _legacy_variant={"outlined-subtle"}
              onClick={() => setCurrentDialogueAction({ action: "" })}
              testId={"worksheet-worksheetAssistant-setCurrentDialogueAction"}
            >
              {DIALOGS_INFO?.[dialogAction]?.buttonOneText({
                t,
                ...dialogParams,
              })}
            </Button>

            <Button
              dsVersion="2.0"
              variant={"destructive"}
              type={"fill"}
              size={"large"}
              _legacy_size={"large"}
              _legacy_variant={"destructive"}
              onClick={onDialogConfirm}
              testId={"worksheet-worksheetAssistant-confirm"}
            >
              {DIALOGS_INFO?.[dialogAction]?.buttonTwoText({
                t,
                ...dialogParams,
              })}
            </Button>
          </footer>
        </Modal.Footer>
      </Modal>

      {promptSuggestionModalInfo?.showModal ? (
        <PromptSuggestionModal
          t={t}
          initialGroupType={_.get(
            promptSuggestionModalInfo,
            "params.groupType"
          )}
          onPromptSuggestionModalClose={() =>
            updatePromptSuggestionModalInfo({ showModal: false })
          }
          onPromptSelection={onPromptSelection}
          constantPrompts={constantPrompts}
          language={aiVoiceModalLanguage}
          isAIResponseLoading={getIsAIResponseLoading(currentAIState)}
          resolvedSubjectGroupNodes={resolvedSubjectGroupNodes}
        />
      ) : null}

      {currentAIState === LOADING || currentAIState === BLOCK_ACTIONS ? (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      ) : null}
    </div>
  );
});

AIAssessmentTools.displayName = "AIAssessmentTools";

AIAssessmentTools.propTypes = {
  isQuestionAssistant: PropTypes.bool,
};

AIAssessmentTools.defaultProps = {
  portalType: "PLANNER",
  isQuestionAssistant: false,
};

const mapStateToProps = (state, ownProps) => {
  const academicYearId = getSelectedAcademicYearId(state);
  const curriculumType = getCurrentCurriculumProgramType(state);
  const userId = getUserId(state);
  const { firstName, lastName, profileImage } = getStaffBasicDetailsFromCache(
    userId
  );
  const userInfo = { userId, firstName, lastName, profileImage };

  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    _.get(state, "teacher.selected_class.selected_course");

  const courseGradeFilters = {
    academicYearId,
  };

  const parentId = _.get(state, "worksheet.parentEntity.id", null);
  const parentType = _.get(state, "worksheet.parentEntity.type", null);

  const worksheetMutationData = {
    worksheet: _.get(state, "worksheet", {}),
    parentId,
    parentType,
  };

  const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
    {
      parentId,
      parentType,
    }
  );

  const aiEnhanceQuestion = _.get(state, "worksheet.aiEnhanceQuestion", {});
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  return {
    curriculumType,
    containerStyle: getWorksheetAIAssessmentToolsStyle(),
    courseId,
    isData: true,
    isLoading: false,
    showLoaderBar: false,
    courseGradeFilters,
    userId,
    plannerElementSets: _.get(state, "platform.currentPlannerElementSets", []),
    worksheetMutationData,
    assessmentQuestionsQueryVariables,
    parentId,
    parentType,
    aiEnhanceQuestion,
    collaboratorsFilter,
    userInfo,
  };
};

const mapActionCreators = {
  showIntercom,
  hideIntercom,
  updateAssignmentField,
  resetAIConversation,
  onSendQuery,
  updateSettings,
  updateAIConversation,
  updateLocalFields,
};

const AIAssessmentToolsComponent = compose(
  I18nHOC({ resource: ["ai", "common", "worksheet"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ assessmentToolConversationId }) =>
      _.isEmpty(assessmentToolConversationId),
    options: ({ assessmentToolConversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: assessmentToolConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getAIConversations,
      ownProps: {
        isData,
        isLoading,
        t,
        assessmentId,
        mode,
        sourceFrom,
        curriculumType,
        courseId,
        selectedCourse,
        courseGradeFilters,
        plannerElementSets,
        i18n,
        isQuestionAssistant,
        collaboratorsFilter,
      },
    }) {
      const queryData = getAIConversationsFromCache(
        getAIConversations.variables
      );

      const networkStatus = _.get(getAIConversations, "networkStatus");
      const messages = [..._.get(queryData, "messages.edges", [])].reverse();

      const pageInfo = _.get(queryData, "messages.pageInfo", {});

      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const endCursor = _.get(pageInfo, "endCursor");

      const conversationMetadata = _.get(queryData, "metadata");
      const conversationAttachments = _.get(queryData, "attachments", []);
      let assessmentDetails = getAssessmentDetailsFromCache({
        id: assessmentId,
        collaboratorsFilter,
      });

      const course = !_.isEmpty(selectedCourse)
        ? selectedCourse
        : getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters,
          });

      const gradeId =
        _.get(course, "primaryGrade.id") || _.get(course?.grades, "0.id");

      const assessmentType = _.get(assessmentDetails, "assessmentType.value");
      const groupType = _.get(assessmentDetails, "type");
      const templateId = _.get(assessmentDetails, "templateId", null);

      const unitPlan = getUnitPlanFromAssessment({ assessmentDetails });

      const unitPlanTemplateId = _.get(unitPlan, "templateId", null);

      const unitPlanDetails = getUnitDetailsFromUnitPlanField({
        assessmentDetails,
      });

      const { unitPlanFields } = unitPlanDetails || {};

      const resolvedSubjectNodes = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      );

      let templates = getPlannerTemplateFromCache(
        {
          ids: getTemplateArray([templateId]),
        },
        true
      );

      if (mode !== "view" && !_.isEmpty(templates)) {
        const unitPlanTemplate = getPlannerTemplateFromCache(
          getVariablesForLocalisedPlannerTemplates({
            templates: [unitPlanTemplateId],
            language: i18n.language,
          })
        );
        templates = [...templates, unitPlanTemplate];
      }

      if (_.isEmpty(templates)) {
        templates = getPlannerTemplateFromCache(
          {
            ids: getTemplateArray([templateId]),
          },
          true
        );
      }

      const originalTemplate = _.get(
        _.find(templates, { id: templateId }),
        "body",
        {}
      );

      const isFromClassroom = getIsFromClassroom({
        sourceFrom,
        template: originalTemplate,
      });

      const dynamicTemplate = updateTemplateForLearningGoals({
        template: _.cloneDeep(originalTemplate),
        templateId,
      });

      const localisedAssessmentTemplate = _.get(
        getLocalisedTemplate({
          template: { body: _.cloneDeep(dynamicTemplate) },
          t,
          templateType: "le",
        }),
        "body",
        {}
      );

      /** As we have learning goals on question level as well, so we are mutating learning goals fields in assessmentDetail.allFields/unitPlanDetails.unitPlanFields and adding question's learningGoals fields */
      if (isQuestionAssistant) {
        const updatedAssessmentFields = replaceLearningGoalsFields({
          template: dynamicTemplate,
          fields: _.get(assessmentDetails, "allFields", []),
          replacementFields: _.get(conversationMetadata, "allFields", []),
        });

        assessmentDetails = update(assessmentDetails, {
          allFields: {
            $set: updatedAssessmentFields,
          },
        });
      }

      const assessmentTemplate = updateAssessmentTemplate({
        t,
        template: localisedAssessmentTemplate,
        assessmentType,
        isFromClassroom,
        curriculumType,
        resourceFields: _.get(assessmentDetails, "allFields", []),
        mode: "edit",
        templateId,
        plannerElementSets,
        subjects: _.get(course, "subjects", []),
        assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
      });

      let _unitPlanTemplate = _.find(templates, { id: unitPlanTemplateId });

      if (_.isEmpty(_unitPlanTemplate) && !_.isEmpty(unitPlanTemplateId)) {
        _unitPlanTemplate = getPlannerTemplateFromCache(
          getVariablesForLocalisedPlannerTemplates({
            templates: [unitPlanTemplateId],
            language: i18n.language,
          })
        );
      }

      const unitPlanTemplate = _.get(
        updateUnitPlanTemplate({
          t,
          template: _unitPlanTemplate,
          unitType: getFieldValue({
            fields: unitPlanDetails?.unitPlanFields,
            uid: "unitType",
          }),
          curriculumType,
          resolvedSubjectNodes,
        }),
        "body",
        {}
      );

      const learningGoalsFields = getLearningGoalsFields(assessmentTemplate);

      return {
        messages,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        conversations: queryData,
        isInitialState: _.isEmpty(messages),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        loadMoreFeed: prepareLoadMoreFeed({
          query: getAIConversationsQuery,
          variables: {
            ...getAIConversations.variables,
            after: endCursor,
          },
          queryData: getAIConversations,
          key: "messages",
        }),
        conversationMetadata,
        conversationAttachments,
        assessmentType,
        gradeId,
        course,
        groupType,
        unitPlanDetails: {
          ...unitPlanDetails,
          unitPlanFields: _.uniqBy(
            [
              ...combineUnitPlanMultiFieldData({
                unitPlanFields: _.cloneDeep(
                  _.get(unitPlanDetails, "unitPlanFields", [])
                ),
                templateBody: assessmentTemplate,
              }),
              ..._.get(unitPlanDetails, "unitPlanFields", []),
            ],
            "uid"
          ),
        },
        unitPlanTemplate,
        assessmentTemplate,
        learningGoalsFields,
        assessmentDetails,
        curriculumType,
        templateId,
      };
    },
  }),
  withLoader
)(AIAssessmentTools);

/**
 *
 * @param {*} props
 * @returns AIAssessmentToolsWrapper
 */
const AIAssessmentToolsWrapper = props => {
  const {
    assessmentId,
    assessmentToolConversationId,
    createAIConversation,
    getAssessmentRefetch,
  } = props;

  useEffect(() => {
    if (!assessmentToolConversationId) {
      const createNewConversation = async () => {
        await createAIConversation({
          input: {
            entityId: assessmentId,
            entityType: "ASSESSMENT",
            entitySubType: "ASSESSMENT_TOOL_CREATION",
          },
        });

        await getAssessmentRefetch();
      };

      createNewConversation();
    }
  }, [assessmentToolConversationId]);

  if (!assessmentToolConversationId)
    return <Loading containerStyle={getWorksheetAIAssessmentToolsStyle()} />;

  return <AIAssessmentToolsComponent {...props} />;
};

export default connect(null, { createAIConversation })(
  AIAssessmentToolsWrapper
);
